import jsPDF from 'jspdf';
import 'jspdf-autotable';

const COMPANY_NAME = 'eCity Infrastructure Solutions';
const COMPANY_ADDRESS = '123 Business Park, Tech City, TC 12345';
const COMPANY_PHONE = '+1-555-ECITY-01';
const COMPANY_EMAIL = 'info@ecity.com';
const COMPANY_WEBSITE = 'www.ecity.com';

const PRIMARY_COLOR = [41, 128, 185];
const ACCENT_COLOR = [52, 152, 219];
const SUCCESS_COLOR = [46, 204, 113];
const WARNING_COLOR = [241, 196, 15];
const ERROR_COLOR = [231, 76, 60];
const GRAY_COLOR = [149, 165, 166];

const addPDFHeader = (doc, title, subtitle = '') => {
  doc?.setFillColor(...PRIMARY_COLOR);
  doc?.rect(0, 0, doc?.internal?.pageSize?.width, 40, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(20);
  doc?.setFont('helvetica', 'bold');
  doc?.text(COMPANY_NAME, 14, 15);
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.text(COMPANY_ADDRESS, 14, 22);
  doc?.text(`${COMPANY_PHONE} | ${COMPANY_EMAIL}`, 14, 27);
  
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  const pageWidth = doc?.internal?.pageSize?.width;
  doc?.text(title, pageWidth - 14, 20, { align: 'right' });
  
  if (subtitle) {
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text(subtitle, pageWidth - 14, 28, { align: 'right' });
  }
  
  doc?.setTextColor(0, 0, 0);
};

const addPDFFooter = (doc, pageNumber, totalPages) => {
  const pageHeight = doc?.internal?.pageSize?.height;
  const pageWidth = doc?.internal?.pageSize?.width;
  
  doc?.setDrawColor(...GRAY_COLOR);
  doc?.line(14, pageHeight - 20, pageWidth - 14, pageHeight - 20);
  
  doc?.setFontSize(8);
  doc?.setTextColor(...GRAY_COLOR);
  doc?.text(`Generated on ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, pageHeight - 12);
  doc?.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 14, pageHeight - 12, { align: 'right' });
  doc?.text(COMPANY_WEBSITE, pageWidth / 2, pageHeight - 12, { align: 'center' });
};

const formatCurrency = (value) => {
  return `OMR ${parseFloat(value || 0)?.toFixed(3)}`;
};

const formatPercentage = (value) => {
  return `${parseFloat(value || 0)?.toFixed(2)}%`;
};

const getRiskColor = (score) => {
  if (score >= 70) return ERROR_COLOR;
  if (score >= 40) return WARNING_COLOR;
  return SUCCESS_COLOR;
};

const getRiskLevel = (score) => {
  if (score >= 70) return 'High Risk';
  if (score >= 40) return 'Medium Risk';
  return 'Low Risk';
};

export const exportWhatIfScenariosPDF = (scenarios, baseConfig) => {
  const doc = new jsPDF();
  let yPos = 50;
  
  addPDFHeader(doc, 'WHAT-IF SCENARIO ANALYSIS', 'Cost Impact Assessment Report');
  
  // Executive Summary
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Executive Summary', 14, yPos);
  yPos += 8;
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text(`Total Scenarios Analyzed: ${scenarios?.length}`, 14, yPos);
  yPos += 6;
  doc?.text(`Analysis Date: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, yPos);
  yPos += 10;
  
  // Key Insights
  const costs = scenarios?.map(s => s?.calculations?.totalCost);
  const profits = scenarios?.map(s => s?.calculations?.grossProfit);
  const margins = scenarios?.map(s => s?.calculations?.profitMarginPercentage);
  
  const minCost = Math.min(...costs);
  const maxCost = Math.max(...costs);
  const bestMargin = Math.max(...margins);
  const costVariance = maxCost - minCost;
  
  doc?.setFillColor(240, 248, 255);
  doc?.roundedRect(14, yPos, 182, 35, 3, 3, 'F');
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Key Insights', 20, yPos + 8);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text(`• Cost Range: ${formatCurrency(minCost)} - ${formatCurrency(maxCost)} (Variance: ${formatCurrency(costVariance)})`, 20, yPos + 16);
  doc?.text(`• Best Profit Margin: ${formatPercentage(bestMargin)} (${scenarios?.find(s => s?.calculations?.profitMarginPercentage === bestMargin)?.name})`, 20, yPos + 23);
  doc?.text(`• Lowest Cost Option: ${formatCurrency(minCost)} (${scenarios?.find(s => s?.calculations?.totalCost === minCost)?.name})`, 20, yPos + 30);
  
  yPos += 45;
  
  // Scenario Comparison Table
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Scenario Comparison', 14, yPos);
  yPos += 8;
  
  const baseScenario = scenarios?.find(s => s?.isBase);
  const baseCalc = baseScenario?.calculations;
  
  const tableData = scenarios?.map(scenario => {
    const costVariance = baseCalc ? ((scenario?.calculations?.totalCost - baseCalc?.totalCost) / baseCalc?.totalCost) * 100 : 0;
    const profitVariance = baseCalc ? ((scenario?.calculations?.grossProfit - baseCalc?.grossProfit) / baseCalc?.grossProfit) * 100 : 0;
    
    return [
      scenario?.name,
      scenario?.deliveryMode?.charAt(0)?.toUpperCase() + scenario?.deliveryMode?.slice(1),
      formatCurrency(scenario?.calculations?.totalCost),
      scenario?.isBase ? 'BASE' : `${costVariance > 0 ? '+' : ''}${formatPercentage(costVariance)}`,
      formatCurrency(scenario?.calculations?.grossProfit),
      formatPercentage(scenario?.calculations?.profitMarginPercentage)
    ];
  });
  
  doc?.autoTable({
    startY: yPos,
    head: [['Scenario', 'Delivery Mode', 'Total Cost', 'Cost Variance', 'Gross Profit', 'Margin %']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 25, halign: 'center' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 25, halign: 'center' }
    }
  });
  
  yPos = doc?.lastAutoTable?.finalY + 15;
  
  // Detailed Scenario Analysis (New Page)
  doc?.addPage();
  yPos = 20;
  
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Detailed Scenario Analysis', 14, yPos);
  yPos += 10;
  
  scenarios?.forEach((scenario, index) => {
    if (yPos > 250) {
      doc?.addPage();
      yPos = 20;
    }
    
    // Scenario Header
    doc?.setFillColor(...ACCENT_COLOR);
    doc?.rect(14, yPos, 182, 8, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text(`${scenario?.name}${scenario?.isBase ? ' (BASE)' : ''}`, 16, yPos + 5.5);
    yPos += 12;
    
    // Configuration Details
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(0, 0, 0);
    doc?.text(`Delivery Mode: ${scenario?.deliveryMode?.charAt(0)?.toUpperCase() + scenario?.deliveryMode?.slice(1)}`, 16, yPos);
    doc?.text(`Overhead: ${formatPercentage(scenario?.config?.overhead)}`, 80, yPos);
    doc?.text(`Margin: ${formatPercentage(scenario?.config?.targetMargin)}`, 130, yPos);
    yPos += 6;
    doc?.text(`Contingency: ${formatPercentage(scenario?.config?.contingency)}`, 16, yPos);
    yPos += 8;
    
    // Financial Breakdown
    const scenarioTableData = [
      ['Direct Costs', formatCurrency(scenario?.calculations?.directCosts)],
      ['Overhead Costs', formatCurrency(scenario?.calculations?.overheadCosts)],
      ['Contingency Costs', formatCurrency(scenario?.calculations?.contingencyCosts)],
      ['Total Cost', formatCurrency(scenario?.calculations?.totalCost)],
      ['Sell Price', formatCurrency(scenario?.calculations?.sellPrice)],
      ['Gross Profit', formatCurrency(scenario?.calculations?.grossProfit)],
      ['Profit Margin', formatPercentage(scenario?.calculations?.profitMarginPercentage)]
    ];
    
    doc?.autoTable({
      startY: yPos,
      body: scenarioTableData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 80, fontStyle: 'bold' },
        1: { cellWidth: 50, halign: 'right', textColor: PRIMARY_COLOR }
      }
    });
    
    yPos = doc?.lastAutoTable?.finalY + 10;
  });
  
  // Risk Assessment Page
  doc?.addPage();
  yPos = 20;
  
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Risk Assessment', 14, yPos);
  yPos += 10;
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text('Each scenario has been evaluated based on profit margins, contingency buffers, cost ratios, and project complexity.', 14, yPos);
  yPos += 15;
  
  scenarios?.forEach((scenario, index) => {
    if (yPos > 250) {
      doc?.addPage();
      yPos = 20;
    }
    
    const riskScore = calculateRiskScore(scenario?.calculations);
    const riskColor = getRiskColor(riskScore?.score);
    const riskLevel = getRiskLevel(riskScore?.score);
    
    // Risk Box
    doc?.setDrawColor(...riskColor);
    doc?.setLineWidth(0.5);
    doc?.rect(14, yPos, 182, 35);
    
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...riskColor);
    doc?.text(`${scenario?.name}: ${riskLevel} (Score: ${riskScore?.score}/100)`, 18, yPos + 8);
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(0, 0, 0);
    
    let factorY = yPos + 16;
    if (riskScore?.factors?.length > 0) {
      doc?.text('Risk Factors:', 18, factorY);
      factorY += 5;
      riskScore?.factors?.forEach(factor => {
        doc?.text(`• ${factor}`, 22, factorY);
        factorY += 5;
      });
    } else {
      doc?.text('No significant risk factors identified.', 18, factorY);
    }
    
    yPos += 40;
  });
  
  // Recommendations Page
  doc?.addPage();
  yPos = 20;
  
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Recommendations', 14, yPos);
  yPos += 10;
  
  doc?.setFillColor(240, 248, 255);
  doc?.roundedRect(14, yPos, 182, 60, 3, 3, 'F');
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...SUCCESS_COLOR);
  doc?.text('Recommended Scenario:', 20, yPos + 8);
  
  const recommendedScenario = scenarios?.reduce((best, current) => {
    const bestScore = (best?.calculations?.profitMarginPercentage * 0.6) - (calculateRiskScore(best?.calculations)?.score * 0.4);
    const currentScore = (current?.calculations?.profitMarginPercentage * 0.6) - (calculateRiskScore(current?.calculations)?.score * 0.4);
    return currentScore > bestScore ? current : best;
  });
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(0, 0, 0);
  doc?.text(recommendedScenario?.name, 20, yPos + 16);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text(`• Profit Margin: ${formatPercentage(recommendedScenario?.calculations?.profitMarginPercentage)}`, 20, yPos + 24);
  doc?.text(`• Total Cost: ${formatCurrency(recommendedScenario?.calculations?.totalCost)}`, 20, yPos + 30);
  doc?.text(`• Gross Profit: ${formatCurrency(recommendedScenario?.calculations?.grossProfit)}`, 20, yPos + 36);
  doc?.text(`• Risk Score: ${calculateRiskScore(recommendedScenario?.calculations)?.score}/100`, 20, yPos + 42);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'italic');
  doc?.setTextColor(...GRAY_COLOR);
  doc?.text('This recommendation balances profitability (60% weight) with risk mitigation (40% weight).', 20, yPos + 52);
  
  yPos += 70;
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(0, 0, 0);
  doc?.text('Key Considerations:', 14, yPos);
  yPos += 8;
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text('• Review delivery mode impact on resource availability and client requirements', 14, yPos);
  yPos += 6;
  doc?.text('• Ensure contingency buffers align with project complexity and risk tolerance', 14, yPos);
  yPos += 6;
  doc?.text('• Validate resource allocations against team capacity and skill requirements', 14, yPos);
  yPos += 6;
  doc?.text('• Consider market conditions and competitive positioning when finalizing pricing', 14, yPos);
  
  // Add footers to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addPDFFooter(doc, i, totalPages);
  }
  
  doc?.save(`What_If_Scenario_Analysis_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
};

const calculateRiskScore = (calculations) => {
  let score = 0;
  let factors = [];
  
  if (calculations?.profitMarginPercentage < 15) {
    score += 30;
    factors?.push('Low profit margin (<15%)');
  } else if (calculations?.profitMarginPercentage < 20) {
    score += 15;
    factors?.push('Moderate profit margin (15-20%)');
  }
  
  const contingencyPercent = (calculations?.contingencyCosts / (calculations?.directCosts + calculations?.overheadCosts)) * 100;
  if (contingencyPercent < 5) {
    score += 20;
    factors?.push('Low contingency buffer (<5%)');
  }
  
  const directCostRatio = calculations?.directCosts / calculations?.totalCost;
  if (directCostRatio > 0.85) {
    score += 25;
    factors?.push('High direct cost ratio (>85%)');
  }
  
  if (calculations?.totalCost > 500000) {
    score += 15;
    factors?.push('High project value (>$500K)');
  }
  
  const overheadRatio = calculations?.overheadCosts / calculations?.directCosts;
  if (overheadRatio < 0.1) {
    score += 10;
    factors?.push('Low overhead allocation (<10%)');
  }
  
  return { score: Math.min(score, 100), factors };
};

export const exportComparisonPDF = (comparisonData, viewMode) => {
  const doc = new jsPDF();
  let yPos = 50;
  
  const reportType = viewMode === 'estimates' ? 'Estimates' : 'Templates';
  addPDFHeader(doc, `${reportType?.toUpperCase()} COMPARISON REPORT`, 'Side-by-Side Analysis with Risk Assessment');
  
  // Executive Summary
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Executive Summary', 14, yPos);
  yPos += 8;
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text(`Total ${reportType} Compared: ${comparisonData?.items?.length}`, 14, yPos);
  yPos += 6;
  doc?.text(`Analysis Date: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, yPos);
  yPos += 10;
  
  // Average Benchmarks
  doc?.setFillColor(240, 248, 255);
  doc?.roundedRect(14, yPos, 182, 30, 3, 3, 'F');
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Average Benchmarks', 20, yPos + 8);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text(`Avg Sell Price: ${formatCurrency(comparisonData?.averages?.sellPrice)}`, 20, yPos + 16);
  doc?.text(`Avg Total Cost: ${formatCurrency(comparisonData?.averages?.totalCost)}`, 20, yPos + 23);
  doc?.text(`Avg Gross Profit: ${formatCurrency(comparisonData?.averages?.grossProfit)}`, 110, yPos + 16);
  
  yPos += 40;
  
  // Comparison Table
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Comparison Overview', 14, yPos);
  yPos += 8;
  
  const tableData = comparisonData?.items?.map(item => {
    const riskLevel = getRiskLevel(item?.riskScore?.score);
    return [
      item?.name || 'Untitled',
      formatCurrency(item?.calculations?.sellPrice),
      `${item?.variance?.sellPrice > 0 ? '+' : ''}${formatPercentage(item?.variance?.sellPrice)}`,
      formatCurrency(item?.calculations?.totalCost),
      formatPercentage(item?.calculations?.profitMarginPercentage),
      `${riskLevel} (${item?.riskScore?.score})`
    ];
  });
  
  doc?.autoTable({
    startY: yPos,
    head: [['Name', 'Sell Price', 'Price Var.', 'Total Cost', 'Margin %', 'Risk Level']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 20, halign: 'center' },
      5: { cellWidth: 35, halign: 'center' }
    }
  });
  
  yPos = doc?.lastAutoTable?.finalY + 15;
  
  // Variance Analysis
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Variance Analysis', 14, yPos);
  yPos += 10;
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(0, 0, 0);
  doc?.text('Variance percentages indicate deviation from average. Positive values (above average) and negative values (below average)', 14, yPos);
  doc?.text('help identify outliers and opportunities for cost optimization or pricing adjustments.', 14, yPos + 5);
  yPos += 15;
  
  const varianceTableData = comparisonData?.items?.map(item => [
    item?.name || 'Untitled',
    `${item?.variance?.sellPrice > 0 ? '+' : ''}${formatPercentage(item?.variance?.sellPrice)}`,
    `${item?.variance?.cost > 0 ? '+' : ''}${formatPercentage(item?.variance?.cost)}`,
    `${item?.variance?.profit > 0 ? '+' : ''}${formatPercentage(item?.variance?.profit)}`
  ]);
  
  doc?.autoTable({
    startY: yPos,
    head: [['Name', 'Sell Price Variance', 'Cost Variance', 'Profit Variance']],
    body: varianceTableData,
    theme: 'grid',
    headStyles: { fillColor: ACCENT_COLOR, fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 40, halign: 'center' },
      2: { cellWidth: 40, halign: 'center' },
      3: { cellWidth: 40, halign: 'center' }
    }
  });
  
  // Detailed Analysis (New Page)
  doc?.addPage();
  yPos = 20;
  
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Detailed Item Analysis', 14, yPos);
  yPos += 10;
  
  comparisonData?.items?.forEach((item, index) => {
    if (yPos > 240) {
      doc?.addPage();
      yPos = 20;
    }
    
    const riskColor = getRiskColor(item?.riskScore?.score);
    const riskLevel = getRiskLevel(item?.riskScore?.score);
    
    // Item Header
    doc?.setFillColor(...ACCENT_COLOR);
    doc?.rect(14, yPos, 182, 8, 'F');
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(255, 255, 255);
    doc?.text(`${index + 1}. ${item?.name || 'Untitled'}`, 16, yPos + 5.5);
    yPos += 12;
    
    // Financial Details
    const itemTableData = [
      ['Sell Price', formatCurrency(item?.calculations?.sellPrice), `Variance: ${item?.variance?.sellPrice > 0 ? '+' : ''}${formatPercentage(item?.variance?.sellPrice)}`],
      ['Total Cost', formatCurrency(item?.calculations?.totalCost), `Variance: ${item?.variance?.cost > 0 ? '+' : ''}${formatPercentage(item?.variance?.cost)}`],
      ['Gross Profit', formatCurrency(item?.calculations?.grossProfit), `Variance: ${item?.variance?.profit > 0 ? '+' : ''}${formatPercentage(item?.variance?.profit)}`],
      ['Profit Margin', formatPercentage(item?.calculations?.profitMarginPercentage), ''],
      ['Direct Costs', formatCurrency(item?.calculations?.directCosts), ''],
      ['Overhead Costs', formatCurrency(item?.calculations?.overheadCosts), ''],
      ['Contingency', formatCurrency(item?.calculations?.contingencyCosts), '']
    ];
    
    doc?.autoTable({
      startY: yPos,
      body: itemTableData,
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold' },
        1: { cellWidth: 40, halign: 'right', textColor: PRIMARY_COLOR },
        2: { cellWidth: 60, fontSize: 7, textColor: GRAY_COLOR }
      }
    });
    
    yPos = doc?.lastAutoTable?.finalY + 5;
    
    // Risk Assessment
    doc?.setDrawColor(...riskColor);
    doc?.setLineWidth(0.5);
    doc?.rect(14, yPos, 182, 25);
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...riskColor);
    doc?.text(`Risk Assessment: ${riskLevel} (Score: ${item?.riskScore?.score}/100)`, 18, yPos + 6);
    
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(0, 0, 0);
    
    if (item?.riskScore?.factors?.length > 0) {
      let factorY = yPos + 12;
      item?.riskScore?.factors?.slice(0, 2)?.forEach(factor => {
        doc?.text(`• ${factor}`, 18, factorY);
        factorY += 5;
      });
    } else {
      doc?.text('No significant risk factors identified.', 18, yPos + 12);
    }
    
    yPos += 30;
  });
  
  // Recommendations Page
  doc?.addPage();
  yPos = 20;
  
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...PRIMARY_COLOR);
  doc?.text('Recommendations & Insights', 14, yPos);
  yPos += 10;
  
  // Best Value Option
  const bestValue = comparisonData?.items?.reduce((best, current) => {
    const bestScore = (best?.calculations?.profitMarginPercentage * 0.5) - (best?.riskScore?.score * 0.3) - (Math.abs(best?.variance?.cost) * 0.2);
    const currentScore = (current?.calculations?.profitMarginPercentage * 0.5) - (current?.riskScore?.score * 0.3) - (Math.abs(current?.variance?.cost) * 0.2);
    return currentScore > bestScore ? current : best;
  });
  
  doc?.setFillColor(...SUCCESS_COLOR);
  doc?.setFillColor(240, 255, 240);
  doc?.roundedRect(14, yPos, 182, 35, 3, 3, 'F');
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(...SUCCESS_COLOR);
  doc?.text('Recommended Option:', 20, yPos + 8);
  
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(0, 0, 0);
  doc?.text(bestValue?.name || 'Untitled', 20, yPos + 16);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text(`Profit Margin: ${formatPercentage(bestValue?.calculations?.profitMarginPercentage)} | Risk Score: ${bestValue?.riskScore?.score}/100`, 20, yPos + 23);
  doc?.text(`Total Cost: ${formatCurrency(bestValue?.calculations?.totalCost)} | Sell Price: ${formatCurrency(bestValue?.calculations?.sellPrice)}`, 20, yPos + 29);
  
  yPos += 45;
  
  // Key Insights
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(0, 0, 0);
  doc?.text('Key Insights:', 14, yPos);
  yPos += 8;
  
  const highRiskItems = comparisonData?.items?.filter(item => item?.riskScore?.score >= 70);
  const lowMarginItems = comparisonData?.items?.filter(item => item?.calculations?.profitMarginPercentage < 15);
  const highVarianceItems = comparisonData?.items?.filter(item => Math.abs(item?.variance?.cost) > 15);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  
  if (highRiskItems?.length > 0) {
    doc?.setTextColor(...ERROR_COLOR);
    doc?.text(`• ${highRiskItems?.length} item(s) identified as high risk - review contingency and margin settings`, 14, yPos);
    yPos += 6;
  }
  
  if (lowMarginItems?.length > 0) {
    doc?.setTextColor(...WARNING_COLOR);
    doc?.text(`• ${lowMarginItems?.length} item(s) have profit margins below 15% - consider pricing adjustments`, 14, yPos);
    yPos += 6;
  }
  
  if (highVarianceItems?.length > 0) {
    doc?.setTextColor(...WARNING_COLOR);
    doc?.text(`• ${highVarianceItems?.length} item(s) show high cost variance (>15%) - investigate cost drivers`, 14, yPos);
    yPos += 6;
  }
  
  doc?.setTextColor(0, 0, 0);
  doc?.text(`• Average profit margin across all items: ${formatPercentage((comparisonData?.items?.reduce((sum, item) => sum + item?.calculations?.profitMarginPercentage, 0) / comparisonData?.items?.length))}`, 14, yPos);
  yPos += 6;
  doc?.text(`• Cost variance range: ${formatPercentage(Math.min(...comparisonData?.items?.map(i => i?.variance?.cost)))} to ${formatPercentage(Math.max(...comparisonData?.items?.map(i => i?.variance?.cost)))}`, 14, yPos);
  
  yPos += 15;
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Action Items:', 14, yPos);
  yPos += 8;
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text('1. Review high-risk items and adjust contingency buffers or resource allocations', 14, yPos);
  yPos += 6;
  doc?.text('2. Standardize pricing strategies for items with similar characteristics', 14, yPos);
  yPos += 6;
  doc?.text('3. Investigate cost drivers for high-variance items to improve estimation accuracy', 14, yPos);
  yPos += 6;
  doc?.text('4. Consider market positioning and competitive factors when finalizing pricing', 14, yPos);
  
  // Add footers to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addPDFFooter(doc, i, totalPages);
  }
  
  doc?.save(`${reportType}_Comparison_Report_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
};
